import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Check } from 'lucide-react';
import { fadeInUp, stagger, scaleIn } from './data';

export const PricingSection = ({ billingCycle, setBillingCycle, activePricingTiers, setContactSalesOpen }) => (
  <section id="pricing" aria-label="Subscription Pricing Plans" className="py-20 md:py-32">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <motion.div
        className="text-center max-w-3xl mx-auto mb-12"
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true }}
        variants={stagger}
      >
        <motion.p variants={fadeInUp} className="text-amber-400 font-semibold mb-4">
          SIMPLE PRICING
        </motion.p>
        <motion.h2 variants={fadeInUp} className="text-3xl md:text-5xl font-bold mb-6 text-white">
          Choose Your Plan
        </motion.h2>
        <motion.p variants={fadeInUp} className="text-gray-400 text-lg mb-8">
          Start with a 7-day free trial. Card required, cancel anytime.
        </motion.p>

        <motion.div variants={fadeInUp} className="flex items-center justify-center gap-4">
          <span className={billingCycle === 'monthly' ? 'text-white' : 'text-gray-500'}>Monthly</span>
          <button
            onClick={() => setBillingCycle(billingCycle === 'monthly' ? 'annual' : 'monthly')}
            className={`relative w-14 h-7 rounded-full transition-colors ${
              billingCycle === 'annual' ? 'bg-amber-500' : 'bg-gray-700'
            }`}
            data-testid="pricing-toggle"
            aria-label="Toggle between monthly and annual pricing"
          >
            <span
              className={`absolute top-0.5 w-6 h-6 bg-white rounded-full shadow transition-transform ${
                billingCycle === 'annual' ? 'left-7' : 'left-0.5'
              }`}
            />
          </button>
          <span className={billingCycle === 'annual' ? 'text-white' : 'text-gray-500'}>
            Annual
          </span>
          {billingCycle === 'annual' && (
            <Badge className="bg-green-500/20 text-green-400 border-green-500/30">
              Save 20%
            </Badge>
          )}
        </motion.div>
      </motion.div>

      <div className="grid md:grid-cols-2 xl:grid-cols-4 gap-6 lg:gap-8">
        {activePricingTiers.map((tier, idx) => (
          <motion.div
            key={tier.name}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={scaleIn}
            transition={{ delay: idx * 0.1 }}
          >
            <Card className={`relative h-full ${
              tier.popular
                ? 'bg-gradient-to-b from-amber-500/10 to-neutral-900 border-amber-500/30'
                : 'bg-neutral-900 border-white/5'
            }`}>
              {tier.popular && (
                <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                  <Badge className="bg-gradient-to-r from-amber-500 to-yellow-500 text-black font-bold">Most Popular</Badge>
                </div>
              )}
              <CardContent className="p-6 md:p-8">
                <h3 className="text-2xl font-bold mb-2 text-white">{tier.name}</h3>
                <p className="text-gray-400 text-sm mb-6">{tier.description}</p>

                <div className="mb-6">
                  {tier.isContactUs ? (
                    <span className="text-3xl md:text-4xl font-black text-amber-400">Custom Pricing</span>
                  ) : (
                    <>
                      <span className="text-4xl md:text-5xl font-black text-amber-400">
                        ${billingCycle === 'annual' ? tier.annual : tier.price}
                      </span>
                      <span className="text-gray-400">/{billingCycle === 'annual' ? 'year' : 'month'}</span>
                    </>
                  )}
                </div>

                {tier.isContactUs ? (
                  <Button
                    onClick={() => setContactSalesOpen(true)}
                    className="w-full h-12 font-bold bg-white/10 hover:bg-white/20 text-white"
                    data-testid={`pricing-cta-${tier.name.toLowerCase()}`}
                  >
                    {tier.cta}
                  </Button>
                ) : (
                  <Link to={`/signup${tier.tier ? `?plan=${tier.tier}` : ''}`}>
                    <Button
                      className={`w-full h-12 font-bold ${
                        tier.popular
                          ? 'bg-gradient-to-r from-amber-500 to-yellow-500 hover:from-amber-400 hover:to-yellow-400 text-black'
                          : 'bg-white/10 hover:bg-white/20 text-white'
                      }`}
                      data-testid={`pricing-cta-${tier.name.toLowerCase()}`}
                    >
                      {tier.cta}
                    </Button>
                  </Link>
                )}

                <ul className="mt-8 space-y-3">
                  {tier.features.map((feature) => (
                    <li key={feature} className="flex items-center gap-3 text-sm">
                      <Check className="w-5 h-5 text-amber-400 flex-shrink-0" />
                      <span className="text-gray-300">{feature}</span>
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>
          </motion.div>
        ))}
      </div>
    </div>
  </section>
);
